import type React from 'react';
import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import { useReferenceTaxonomyVersions } from '../selection-lists/hooks/useReferenceTaxonomyVersions';
import { BaseTaxonomyIdSelect } from './BaseTaxonomyIdSelect';
import { useCustomTaxonomyAllowlist } from './utils/customTaxonomyAllowlist';
import { getPrettyTaxonomyName } from './utils/roleTaxonomy';

type SkillTaxonomyCommonProps = {
  isDisabled?: boolean;
  includeCustom?: boolean;
  currentResolvedAdaptiveLabel?: string;
  variant?: 'default' | 'compact';
  usePortal?: boolean;
  selectedLabelPrefix?: string;
};

type SkillTaxonomyPropsWithAdaptive = SkillTaxonomyCommonProps & {
  includeAdaptive: true;
  value?: number | 'adaptive';
  onChange: (taxonomyId: number | 'adaptive') => void;
};

type SkillTaxonomyPropsWithoutAdaptive = SkillTaxonomyCommonProps & {
  includeAdaptive?: false | undefined;
  value?: number;
  onChange: (taxonomyId: number) => void;
};

// Accept dynamic boolean flags by widening value/onChange when boolean is used
type SkillTaxonomyPropsWithDynamicFlag = SkillTaxonomyCommonProps & {
  includeAdaptive: boolean;
  value?: number | 'adaptive';
  onChange: (taxonomyId: number | 'adaptive') => void;
};

type SkillTaxonomyIdSelectProps =
  | SkillTaxonomyPropsWithAdaptive
  | SkillTaxonomyPropsWithoutAdaptive
  | SkillTaxonomyPropsWithDynamicFlag;

const SkillTaxonomyIdSelect: React.FC<SkillTaxonomyIdSelectProps> = (props) => {
  const {
    isDisabled = false,
    currentResolvedAdaptiveLabel,
    includeCustom = false,
    variant = 'default',
    usePortal = false,
    selectedLabelPrefix,
  } = props;
  const { taxonomies } = useReferenceTaxonomyVersions({
    taxonomyType: TaxonomyType.Skill,
  });
  const { skill: allowedCustomIds } = useCustomTaxonomyAllowlist();

  const taxonomyIds = useMemo(() => {
    if (!taxonomies || taxonomies.length === 0) return undefined;

    return taxonomies
      .filter((t: { is_latest: boolean }) => t.is_latest)
      .map((t: { id: number }) => t.id);
  }, [taxonomies]);

  const customTaxonomies = useMemo(() => {
    const result: Array<{ id: number; label: string }> = [];
    if (!taxonomies || taxonomies.length === 0) return result;

    allowedCustomIds.forEach((id) => {
      const meta = taxonomies.find(
        (t: { id: number; is_latest: boolean }) => t.id === id && t.is_latest
      );
      if (meta) {
        result.push({ id, label: getPrettyTaxonomyName(meta.name) });
      }
    });
    return result;
  }, [taxonomies, allowedCustomIds]);

  if (props.includeAdaptive) {
    return (
      <BaseTaxonomyIdSelect
        includeAdaptive
        value={props.value}
        onChange={props.onChange}
        isDisabled={isDisabled}
        currentResolvedAdaptiveLabel={currentResolvedAdaptiveLabel}
        includeCustom={includeCustom}
        variant={variant}
        usePortal={usePortal}
        taxonomyIds={taxonomyIds}
        customTaxonomies={customTaxonomies}
        selectedLabelPrefix={selectedLabelPrefix}
      />
    );
  }

  return (
    <BaseTaxonomyIdSelect
      value={props.value as number | undefined}
      onChange={props.onChange}
      isDisabled={isDisabled}
      currentResolvedAdaptiveLabel={currentResolvedAdaptiveLabel}
      includeCustom={includeCustom}
      variant={variant}
      usePortal={usePortal}
      taxonomyIds={taxonomyIds}
      customTaxonomies={customTaxonomies}
      selectedLabelPrefix={selectedLabelPrefix}
    />
  );
};

export default SkillTaxonomyIdSelect;
